import { GTM_ID, META_PIXEL_ID } from '../data/site'

type EventPayload = Record<string, string | number | boolean | undefined>

declare global {
  interface Window {
    dataLayer?: unknown[]
    fbq?: (...args: unknown[]) => void
    _fbq?: unknown
  }
}

const isPlaceholder = (id: string) => id.includes('X') || /^0+$/.test(id)

let initialized = false

/**
 * Inyecta GTM y el Pixel de Meta en el <head>.
 * Se llama en index.tsx ANTES de renderizar React para que el Pixel cargue lo antes posible.
 */
export function initTracking(): void {
  if (initialized || typeof window === 'undefined') return
  initialized = true

  window.dataLayer = window.dataLayer || []

  if (!isPlaceholder(GTM_ID) && !document.getElementById('gtm-script')) {
    const script = document.createElement('script')
    script.id = 'gtm-script'
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
    document.head.appendChild(script)
  }

  if (!isPlaceholder(META_PIXEL_ID) && !document.getElementById('meta-pixel')) {
    const script = document.createElement('script')
    script.id = 'meta-pixel'
    script.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;
n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('init','${META_PIXEL_ID}');fbq('track','PageView');`
    document.head.appendChild(script)

    const noscript = document.createElement('noscript')
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1" alt="" />`
    document.head.appendChild(noscript)
  }
}

/** Envía un evento a dataLayer (GTM) y como evento personalizado al Pixel. */
export function trackEvent(event: string, payload: EventPayload = {}): void {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event, ...payload })
  if (typeof window.fbq === 'function') window.fbq('trackCustom', event, payload)
}

/**
 * EVENTO CRÍTICO: Lead estándar de Meta.
 * Se dispara ÚNICAMENTE cuando el usuario abre WhatsApp desde un botón.
 * Si tenemos su email, se envía como Advanced Matching (Meta lo hashea en el navegador)
 * para mejorar la calidad de coincidencia de la campaña.
 */
export function trackWhatsAppLead(location: string, intent: string, email?: string): void {
  if (typeof window === 'undefined') return
  const payload = { cta_location: location, cta_intent: intent }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event: 'whatsapp_click', ...payload, lead_email: email || undefined })

  if (typeof window.fbq === 'function') {
    if (email && !isPlaceholder(META_PIXEL_ID)) {
      window.fbq('init', META_PIXEL_ID, { em: email.trim().toLowerCase() })
    }
    window.fbq('track', 'Lead', { content_name: 'WhatsApp', content_category: intent, ...payload }, {
      eventID: `wa_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    })
  }
}

export const trackPhoneClick = (location: string) => trackEvent('phone_click', { cta_location: location })

/** Envío de formulario: evento personalizado (NO Lead) para no inflar la conversión de la campaña. */
export const trackFormSubmit = (payload: EventPayload) => trackEvent('form_submit', payload)

/** Email capturado antes de abrir WhatsApp: GTM puede reenviarlo a tu herramienta de email marketing. */
export const trackEmailCaptured = (email: string, location: string) =>
  trackEvent('email_captured', { lead_email: email, cta_location: location })
